import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    FieldError,
    Input,
    Label,
    TextField,
    Typography,
} from '@heroui/react';
import { FormDrawer } from '@/components/formDrawer';
import { getNodeTestIdProp } from '@/utils';
import { buildVariantLabel } from './productDetailMeta';
import { VariantBarcodesAddRow } from './VariantBarcodesAddRow';
import { VariantBarcodesAlternates } from './VariantBarcodesAlternates';
import { VariantBarcodesDiscardDialog } from './VariantBarcodesDiscardDialog';
import type { VariantBarcodesEditor } from './useVariantBarcodesForm';
import type { BarcodeFieldError } from './variantBarcodesFormMeta';

type VariantBarcodesDrawerProps = {
    editor: VariantBarcodesEditor;
};

/**
 * The barcodes drawer (bos-app#224, US-14), in the shared `FormDrawer`: the
 * main barcode, each alternate with **Make main** and **Remove** (named for
 * its code; `VariantBarcodesAlternates`, one `VariantBarcodeAlternate`
 * each), and "Add a barcode" with **Add** (`VariantBarcodesAddRow`: Enter
 * adds too, as a scanner sends it). **Save barcodes** sends the whole set;
 * an error shows under the field it belongs to. Closing with unsaved edits
 * asks first, in `VariantBarcodesDiscardDialog` over the drawer. Render-only:
 * `useVariantBarcodesForm` owns the draft and the first-invalid focus,
 * through each input's ref (`inputRefOf`).
 */
export const VariantBarcodesDrawer: React.FC<VariantBarcodesDrawerProps> = ({
    editor,
}) => {
    const { t } = useTranslation('pages');
    const {
        variant,
        isOpen,
        draft,
        errors,
        isDirty,
        isSaving,
        inputRefOf,
        isDiscarding,
        onClose,
        onKeepEditing,
        onDiscard,
        onMainChange,
        onFieldBlur,
        onSave,
    } = editor;

    const errorText = (error: BarcodeFieldError | undefined) =>
        error === undefined ? null : t(error.key, error.values);

    const variantLabel = variant === null ? '' : buildVariantLabel(variant);

    return (
        <>
            <FormDrawer
                footer={
                    <>
                        <Button
                            isDisabled={isSaving}
                            onPress={onClose}
                            variant="tertiary"
                            {...getNodeTestIdProp('variant-barcodes-cancel')}
                        >
                            {t('products.detail.barcodes.cancel')}
                        </Button>
                        <Button
                            isDisabled={!isDirty}
                            isPending={isSaving}
                            onPress={onSave}
                            variant="primary"
                            {...getNodeTestIdProp('variant-barcodes-save')}
                        >
                            {isSaving
                                ? t('products.detail.barcodes.saving')
                                : t('products.detail.barcodes.save')}
                        </Button>
                    </>
                }
                isOpen={isOpen}
                onClose={onClose}
                subtitle={t('products.detail.barcodes.intro')}
                testId="variant-barcodes"
                title={t('products.detail.barcodes.drawerTitle', {
                    variant: variantLabel,
                })}
            >
                <TextField
                    className="flex w-full flex-col gap-1"
                    isDisabled={isSaving}
                    isInvalid={errors.main !== undefined}
                    isRequired
                    onBlur={() => onFieldBlur('main')}
                    onChange={onMainChange}
                    value={draft.main}
                    {...getNodeTestIdProp('variant-barcodes-main')}
                >
                    <Label>{t('products.detail.barcodes.mainLabel')}</Label>
                    <Input ref={inputRefOf('main')} />
                    <FieldError>{errorText(errors.main)}</FieldError>
                </TextField>

                <VariantBarcodesAlternates editor={editor} />

                <VariantBarcodesAddRow editor={editor} />

                <Typography color="muted" type="body-xs">
                    {t('products.detail.barcodes.shared')}
                </Typography>
            </FormDrawer>
            <VariantBarcodesDiscardDialog
                isOpen={isDiscarding}
                onDiscard={onDiscard}
                onKeepEditing={onKeepEditing}
            />
        </>
    );
};
